import {Observable} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {of} from 'rxjs/internal/observable/of';

import {
    ApiError,
    ApiResponse,
    AddressTypesEnum,
    HttpStatusCodesEnum,
    ClientStatusTriggersEnum,
    AuthApiService,
    CompaniesService,
    EnrollmentsService,
    CompanyAddressesService,
    CompanyTaxDetailsService,
    CountryModel,
    CompanyDocumentModel,
    CompanyExtendedModel,
    CompanyStatusTriggerModel,
    CompanyDocumentsAtomicService
} from '@spa/api';

import {Helper} from '../../../../../shared/classes';

import {CompanyRegistrationTrait} from '../../../traits';

import {
    PanelClientsAddCorporateStepsEnum,
    CompanyVatDetailsFormErrorStatesEnum,
    CompanyRegistrationFormErrorStatesEnum,
} from '../../../enums';

import {CompanyForm} from './company.form';
import {CompanyAddressForm} from './company-address.form';
import {CompanyVatDetailsForm} from './company-vat-details.form';
import {CompanyTaxDetailsForm} from './company-tax-details.form';
import {CompanyDocumentsForm} from './company-documents.form';
import {CompanyProofOfAddressForm} from './company-proof-of-address.form';

export class CompanyRegistrationForm {
    private readonly userId: number;
    private readonly countries: CountryModel[];
    private readonly companiesService: CompaniesService;
    private readonly enrollmentsService: EnrollmentsService;
    private readonly taxDetailsService: CompanyTaxDetailsService;
    private readonly addressesService: CompanyAddressesService;
    private readonly authApiService: AuthApiService;
    private readonly company: CompanyExtendedModel;
    private readonly companyDocumentsAtomicService: CompanyDocumentsAtomicService;

    public companyForm: CompanyForm;
    public taxDetailsForm: CompanyTaxDetailsForm;
    public vatDetailsForm: CompanyVatDetailsForm;
    public residentialAddressForm: CompanyAddressForm;
    public mailingAddressForm: CompanyAddressForm;
    public documentsForm: CompanyDocumentsForm;
    public proofOfAddressForm: CompanyProofOfAddressForm;

    constructor(
        userId: number,
        company: CompanyExtendedModel,
        countries: CountryModel[],
        companiesService: CompaniesService,
        enrollmentsService: EnrollmentsService,
        taxDetailsService: CompanyTaxDetailsService,
        addressesService: CompanyAddressesService,
        authApiService: AuthApiService,
        companyDocumentsAtomicService: CompanyDocumentsAtomicService
    ) {
        this.userId = userId;
        this.company = company;
        this.countries = countries;
        this.companiesService = companiesService;
        this.enrollmentsService = enrollmentsService;
        this.taxDetailsService = taxDetailsService;
        this.addressesService = addressesService;
        this.authApiService = authApiService;
        this.companyDocumentsAtomicService = companyDocumentsAtomicService;
    }

    private initCompanyStep(): void {
        this.companyForm = new CompanyForm(this.userId, this.company, this.companiesService, this.enrollmentsService);

        this.companyForm.initForm(this.countries, true);
    }

    private createCompanyRequest(): Observable<ApiResponse<ApiError[] | boolean>> {
        return this.companyForm.createRequest().pipe(
            map((response: ApiResponse<ApiError[] | boolean>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    const mappedErrors = CompanyRegistrationTrait.getMappedCompanyErrors(response.data as ApiError[]);

                    response.setData(mappedErrors);
                }

                return response;
            })
        );
    }

    private initDocumentDetailsStep(): void {
        this.taxDetailsForm = new CompanyTaxDetailsForm(this.company, this.taxDetailsService);
        this.vatDetailsForm = new CompanyVatDetailsForm(this.company, this.companiesService);

        this.taxDetailsForm.initForm(this.countries);
        this.vatDetailsForm.initForm();
    }

    private createDocumentDetailsRequest(): Observable<ApiResponse<ApiError[] | boolean>> {
        return this.taxDetailsForm.createRequest().pipe(
            switchMap((taxDetailsResponse: ApiResponse<ApiError[] | boolean>) => {
                if (taxDetailsResponse.status !== HttpStatusCodesEnum.Ok) {
                    const mappedErrors = CompanyRegistrationTrait.getMappedTaxDetailsErrors(
                        taxDetailsResponse.data as ApiError[]
                    );

                    taxDetailsResponse.setData(mappedErrors);

                    return of(taxDetailsResponse);
                }

                return this.vatDetailsForm.createRequest().pipe(
                    map((vatDetailsResponse: ApiResponse<ApiError[] | boolean>) => {
                        if (vatDetailsResponse.status !== HttpStatusCodesEnum.Ok) {
                            const mappedErrors = (vatDetailsResponse.data as ApiError[]).map((error: ApiError) => {
                                if (error.status !== CompanyVatDetailsFormErrorStatesEnum.VatDetailsUpdateFailed) {
                                    return error;
                                }

                                return new ApiError(CompanyRegistrationFormErrorStatesEnum.VatDetailsUpdateFailed);
                            });

                            vatDetailsResponse.setData(mappedErrors);
                        }

                        return vatDetailsResponse;
                    })
                );
            })
        );
    }

    private initAddressesStep(): void {
        this.residentialAddressForm = new CompanyAddressForm(
            this.company,
            this.company.getAddressByType(AddressTypesEnum.Residential),
            this.addressesService
        );

        this.mailingAddressForm = new CompanyAddressForm(
            this.company,
            this.company.getAddressByType(AddressTypesEnum.Mailing),
            this.addressesService
        );

        this.residentialAddressForm.initForm(AddressTypesEnum.Residential, this.countries);
    }

    private createAddressesRequest(): Observable<ApiResponse<ApiError[] | boolean>> {
        return this.residentialAddressForm.createRequest().pipe(
            switchMap((residentialAddressResponse: ApiResponse<ApiError[] | boolean>) => {
                if (residentialAddressResponse.status !== HttpStatusCodesEnum.Ok) {
                    const mappedErrors = CompanyRegistrationTrait.getMappedResidentialAddressErrors(
                        residentialAddressResponse.data as ApiError[]
                    );

                    residentialAddressResponse.setData(mappedErrors);

                    return of(residentialAddressResponse);
                }

                if (!this.mailingAddressForm.form) {
                    return of(residentialAddressResponse);
                }

                return this.mailingAddressForm.createRequest().pipe(
                    map((mailingAddressResponse: ApiResponse<ApiError[] | boolean>) => {
                        if (mailingAddressResponse.status !== HttpStatusCodesEnum.Ok) {
                            const mappedErrors = CompanyRegistrationTrait.getMappedMailingAddressErrors(
                                mailingAddressResponse.data as ApiError[]
                            );

                            mailingAddressResponse.setData(mappedErrors);
                        }

                        return mailingAddressResponse;
                    })
                );
            })
        );
    }

    private initUploadDocumentsStep(): void {
        this.documentsForm = new CompanyDocumentsForm(
            this.company,
            this.authApiService,
            this.companyDocumentsAtomicService
        );

        this.proofOfAddressForm = new CompanyProofOfAddressForm(
            this.company,
            this.authApiService,
            this.companyDocumentsAtomicService
        );

        this.documentsForm.initFormControl();
        this.documentsForm.initDocument(this.company.documents);

        this.proofOfAddressForm.initFormControl();
        this.proofOfAddressForm.initDocument(this.company.documents);
    }

    private createUploadDocumentsRequest(): Observable<ApiResponse<ApiError[] | boolean>> {
        return this.documentsForm.createRequest(false)
            .pipe(
                switchMap((documentsResponse: ApiResponse<ApiError[] | CompanyDocumentModel>) => {
                    if (documentsResponse.status !== HttpStatusCodesEnum.Ok) {
                        const mappedErrors = CompanyRegistrationTrait.getMappedDocumentErrors(
                            documentsResponse.data as ApiError[]
                        );

                        const errorResponse = new ApiResponse<ApiError[]>(HttpStatusCodesEnum.BadRequest, mappedErrors);

                        return of(errorResponse);
                    }

                    return this.proofOfAddressForm.createRequest(false).pipe(
                        map((proofOfAddressDocumentResponse: ApiResponse<ApiError[] | CompanyDocumentModel>) => {
                            if (documentsResponse.status !== HttpStatusCodesEnum.Ok) {
                                const mappedErrors = CompanyRegistrationTrait.getMappedProofOfAddressDocumentErrors(
                                    proofOfAddressDocumentResponse.data as ApiError[]
                                );

                                return new ApiResponse<ApiError[]>(HttpStatusCodesEnum.BadRequest, mappedErrors);
                            }

                            return Helper.createSuccessResponse<boolean>(true);
                        })
                    );
                })
            );
    }

    private changeCompanyStatus(trigger: number): Observable<ApiResponse<ApiError[] | boolean>> {
        const currentCompanyStatusTrigger = Helper.enumsHelper().getClientStatusTriggerByStatus(this.company.status);

        if (trigger === currentCompanyStatusTrigger) {
            return Helper.createSuccessResponseObservable<boolean>(true);
        }

        const model = new CompanyStatusTriggerModel(this.company.id, trigger);

        return this.companiesService.updateStatus(model).pipe(
            map((response: ApiResponse<ApiError[] | null>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    let state = null;

                    switch (trigger) {
                        case ClientStatusTriggersEnum.PendingDocuments:
                            state = CompanyRegistrationFormErrorStatesEnum.UpdateStatusToPendingFailed;
                            break;
                        case ClientStatusTriggersEnum.PendingAcceptance:
                            state = CompanyRegistrationFormErrorStatesEnum.UpdateStatusToPendingAcceptanceFailed;
                            break;
                    }

                    const errorResponse =
                        new ApiResponse<ApiError[]>(HttpStatusCodesEnum.BadRequest, []);

                    if (!state) {
                        return errorResponse;
                    }

                    errorResponse.setData([new ApiError(state)]);

                    return errorResponse;
                }

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    initForm(step: number): void {
        switch (step) {
            case PanelClientsAddCorporateStepsEnum.Company:
                this.initCompanyStep();
                break;
            case PanelClientsAddCorporateStepsEnum.DocumentDetails:
                this.initDocumentDetailsStep();
                break;
            case PanelClientsAddCorporateStepsEnum.Addresses:
                this.initAddressesStep();
                break;
            case PanelClientsAddCorporateStepsEnum.UploadDocuments:
                this.initUploadDocumentsStep();
                break;

        }
    }

    validateForm(
        step: number,
        messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }
    ): boolean {
        switch (step) {
            case PanelClientsAddCorporateStepsEnum.Company:
                return this.companyForm.validateForm(messageFunction);
            case PanelClientsAddCorporateStepsEnum.DocumentDetails:
                const taxDetailsCheck = this.taxDetailsForm.validateForm(this.countries, messageFunction);
                const vatDetailsCheck = this.vatDetailsForm.validateForm(messageFunction);

                return taxDetailsCheck && vatDetailsCheck;
            case PanelClientsAddCorporateStepsEnum.Addresses:
                const residentialAddressCheck = this.residentialAddressForm.validateForm(messageFunction);

                if (this.mailingAddressForm.form) {
                    const mailingAddressCheck = this.mailingAddressForm.validateForm(messageFunction);

                    return residentialAddressCheck && mailingAddressCheck;
                }

                return residentialAddressCheck;
            case PanelClientsAddCorporateStepsEnum.UploadDocuments:
                const documentsCheck = this.documentsForm.validateForm(messageFunction);
                const proofAddressFormCheck = this.proofOfAddressForm.validateForm(messageFunction);

                return documentsCheck && proofAddressFormCheck;
        }

        return false;
    }


    createRequest(step: number): Observable<ApiResponse<ApiError[] | boolean>> {
        switch (step) {
            case PanelClientsAddCorporateStepsEnum.Company:
                return this.createCompanyRequest();
            case PanelClientsAddCorporateStepsEnum.DocumentDetails:
                return this.createDocumentDetailsRequest();
            case PanelClientsAddCorporateStepsEnum.Addresses:
                return this.createAddressesRequest()
                    .pipe(
                        switchMap((response: ApiResponse<ApiError[] | boolean>) => {
                            if (response.status !== HttpStatusCodesEnum.Ok) {
                                return of(response);
                            }

                            return this.changeCompanyStatus(ClientStatusTriggersEnum.PendingDocuments);
                        })
                    );
            case PanelClientsAddCorporateStepsEnum.UploadDocuments:
                return this.createUploadDocumentsRequest().pipe(
                    switchMap((response: ApiResponse<ApiError[] | boolean>) => {
                        if (response.status !== HttpStatusCodesEnum.Ok) {
                            return of(response);
                        }

                        return this.changeCompanyStatus(ClientStatusTriggersEnum.PendingAcceptance);
                    })
                );
        }

        return null;
    }
}
